import { request } from './api-client';
import type {
  AdminActivationBatch,
  AdminCoupon,
  AdminOverview,
  AdminProduct,
  AdminStudent,
  AdminOrder,
  AdminBankTransaction,
  AdminCourseCurriculum,
  AdminSkillVersion,
  AdminWorkshop,
  AdminWorkshopRegistration,
  Cart,
  CheckoutResult,
  CouponValidation,
  HomepageSettings,
  HomepageSettingsInput,
  LibraryEntry,
  Order,
  Playback,
  ProductDetail,
  ProductSummary,
  ProductType,
  Profile,
  SkillDownload,
  WorkshopDetail,
  WorkshopRegistrationInput,
  WorkshopSummary,
  CreateActivationBatchInput,
  CreateAdminCouponInput,
  CreateAdminProductInput,
  CreatedActivationBatch,
  CreateAdminWorkshopInput,
  StreamDirectUpload,
} from './types';

/** One function per endpoint in AGENT_HANDOFF.md. Nothing else calls fetch. */
export const api = {
  health: () => request<{ status: string; database: string }>('/health'),

  homepage: () => request<HomepageSettings>('/homepage'),

  // ---- Public catalog -----------------------------------------------------
  products: (filters: { type?: ProductType; featured?: boolean } = {}) =>
    request<ProductSummary[]>('/products', {
      query: { type: filters.type, featured: filters.featured },
    }),

  product: (slug: string) => request<ProductDetail>(`/products/${encodeURIComponent(slug)}`),

  featuredWorkshops: () => request<WorkshopSummary[]>('/workshops/featured'),

  workshop: (slug: string) => request<WorkshopDetail>(`/workshops/${encodeURIComponent(slug)}`),

  /**
   * Public — no sign-in required. The token is still sent when a session
   * exists so the backend can link the registration to the profile.
   */
  registerWorkshop: (slug: string, input: WorkshopRegistrationInput) =>
    request<unknown>(`/workshops/${encodeURIComponent(slug)}/registrations`, {
      method: 'POST',
      body: input,
      auth: 'optional',
    }),

  // ---- Account ------------------------------------------------------------
  me: () => request<Profile>('/me', { auth: 'required' }),

  library: () => request<LibraryEntry[]>('/me/library', { auth: 'required' }),

  // ---- Cart ---------------------------------------------------------------
  cart: () => request<Cart>('/cart', { auth: 'required' }),

  addToCart: (productId: string) =>
    request<Cart>('/cart/items', { method: 'POST', body: { productId }, auth: 'required' }),

  removeFromCart: (productId: string) =>
    request<Cart>(`/cart/items/${productId}`, { method: 'DELETE', auth: 'required' }),

  // ---- Coupons and checkout ----------------------------------------------
  /** Preview only. The authoritative discount comes back from `checkout`. */
  validateCoupon: (code: string) =>
    request<CouponValidation>('/coupons/validate', {
      method: 'POST',
      body: { code },
      auth: 'required',
    }),

  checkout: (couponCode?: string) =>
    request<CheckoutResult>('/checkout', {
      method: 'POST',
      body: couponCode ? { couponCode } : {},
      auth: 'required',
    }),

  /** Poll this after checkout; the return page is never proof of payment. */
  order: (orderId: string) => request<Order>(`/orders/${orderId}`, { auth: 'required' }),

  redeemActivationCode: (code: string) =>
    request<unknown>('/activation-codes/redeem', {
      method: 'POST',
      body: { code },
      auth: 'required',
    }),

  // ---- Owned content ------------------------------------------------------
  /** Returns a short-lived signed URL; open it immediately, never cache it. */
  downloadSkill: (skillId: string) =>
    request<SkillDownload>(`/skills/${skillId}/download`, { method: 'POST', auth: 'required' }),

  /** Request a fresh token every time a lesson is opened. */
  lessonPlayback: (courseId: string, lessonId: string) =>
    request<Playback>(`/courses/${courseId}/lessons/${lessonId}/playback`, { auth: 'required' }),

  previewPlayback: (courseId: string, lessonId: string) =>
    request<Playback>(`/courses/${courseId}/lessons/${lessonId}/preview-playback`),

  // ---- Admin --------------------------------------------------------------
  adminOverview: () => request<AdminOverview>('/admin/overview', { auth: 'required' }),

  adminProducts: (type?: ProductType) =>
    request<AdminProduct[]>('/admin/products', {
      query: { type },
      auth: 'required',
    }),

  createAdminProduct: (input: CreateAdminProductInput) =>
    request<AdminProduct>('/admin/products', {
      method: 'POST',
      body: input,
      auth: 'required',
    }),

  updateAdminProduct: (id: string, input: Partial<CreateAdminProductInput>) =>
    request<AdminProduct>(`/admin/products/${id}`, {
      method: 'PATCH',
      body: input,
      auth: 'required',
    }),

  uploadAdminProductThumbnail: (id: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<{ thumbnailUrl: string }>(`/admin/products/${id}/thumbnail`, {
      method: 'POST',
      body,
      auth: 'required',
    });
  },

  adminStudents: () => request<AdminStudent[]>('/admin/students', { auth: 'required' }),

  adminCoupons: () => request<AdminCoupon[]>('/admin/coupons', { auth: 'required' }),

  createAdminCoupon: (input: CreateAdminCouponInput) =>
    request<AdminCoupon>('/admin/coupons', {
      method: 'POST',
      body: input,
      auth: 'required',
    }),

  updateAdminCouponStatus: (id: string, status: 'ACTIVE' | 'INACTIVE') =>
    request<AdminCoupon>(`/admin/coupons/${id}`, {
      method: 'PATCH',
      body: { status },
      auth: 'required',
    }),

  adminActivationBatches: () =>
    request<AdminActivationBatch[]>('/admin/activation-code-batches', { auth: 'required' }),

  createAdminActivationBatch: (input: CreateActivationBatchInput) =>
    request<CreatedActivationBatch>('/admin/activation-code-batches', {
      method: 'POST',
      body: input,
      auth: 'required',
    }),

  adminOrders: () => request<AdminOrder[]>('/admin/orders', { auth: 'required' }),

  adminBankTransactions: () =>
    request<AdminBankTransaction[]>('/admin/bank-transactions', { auth: 'required' }),

  reviewAdminBankTransaction: (
    id: string,
    input: { action: 'MATCH_ORDER' | 'IGNORE'; orderId?: string; note?: string },
  ) => request<unknown>(`/admin/bank-transactions/${id}/review`, {
    method: 'PATCH', body: input, auth: 'required',
  }),

  adminCourseCurriculum: (courseId: string) =>
    request<AdminCourseCurriculum>(`/admin/courses/${courseId}/curriculum`, { auth: 'required' }),

  createAdminCourseSection: (courseId: string, input: { title: string; position: number }) =>
    request<unknown>(`/admin/courses/${courseId}/sections`, {
      method: 'POST', body: input, auth: 'required',
    }),

  createAdminLesson: (
    sectionId: string,
    input: { title: string; description?: string; position: number; isPreview?: boolean },
  ) => request<unknown>(`/admin/sections/${sectionId}/lessons`, {
    method: 'POST', body: input, auth: 'required',
  }),

  updateAdminLesson: (lessonId: string, input: { title?: string; description?: string; position?: number; isPreview?: boolean }) =>
    request<unknown>(`/admin/lessons/${lessonId}`, {
      method: 'PATCH', body: input, auth: 'required',
    }),

  createLessonVideoUpload: (lessonId: string) =>
    request<StreamDirectUpload>(`/admin/lessons/${lessonId}/video-upload-url`, {
      method: 'POST', auth: 'required',
    }),

  uploadVideoToStream: async (uploadUrl: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(uploadUrl, { method: 'POST', body });
    if (!response.ok) throw new Error('Không tải được video lên Cloudflare Stream.');
  },

  refreshLessonVideoStatus: (lessonId: string) =>
    request<unknown>(`/admin/lessons/${lessonId}/video-status`, {
      method: 'POST', auth: 'required',
    }),

  adminSkillVersions: (skillId: string) =>
    request<AdminSkillVersion[]>(`/admin/skills/${skillId}/versions`, { auth: 'required' }),

  uploadAdminSkillVersion: (skillId: string, version: string, changelog: string, file: File) => {
    const body = new FormData();
    body.append('version', version);
    body.append('changelog', changelog);
    body.append('file', file);
    return request<AdminSkillVersion>(`/admin/skills/${skillId}/upload`, {
      method: 'POST', body, auth: 'required',
    });
  },

  adminWorkshops: () => request<AdminWorkshop[]>('/admin/workshops', { auth: 'required' }),

  createAdminWorkshop: (input: CreateAdminWorkshopInput) =>
    request<AdminWorkshop>('/admin/workshops', { method: 'POST', body: input, auth: 'required' }),

  updateAdminWorkshopStatus: (id: string, status: AdminWorkshop['status']) =>
    request<AdminWorkshop>(`/admin/workshops/${id}/status`, {
      method: 'PATCH', body: { status }, auth: 'required',
    }),

  adminWorkshopRegistrations: (id: string) =>
    request<AdminWorkshopRegistration[]>(`/admin/workshops/${id}/registrations`, {
      auth: 'required',
    }),

  updateAdminWorkshopRegistrationStatus: (
    id: string,
    status: AdminWorkshopRegistration['status'],
  ) => request<AdminWorkshopRegistration>(`/admin/workshop-registrations/${id}/status`, {
    method: 'PATCH', body: { status }, auth: 'required',
  }),

  adminHomepage: () => request<HomepageSettings>('/admin/homepage', { auth: 'required' }),

  updateAdminHomepage: (input: HomepageSettingsInput) =>
    request<HomepageSettings>('/admin/homepage', {
      method: 'PATCH',
      body: input,
      auth: 'required',
    }),

  uploadHomepageMedia: (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<{ mediaUrl: string; mediaType: 'IMAGE' | 'VIDEO' }>(
      '/admin/homepage/media',
      { method: 'POST', body, auth: 'required' },
    );
  },
};
